import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import ExitPopup940 from '@/components/ui/ExitPopup940';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form941MailAddress = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 940 Mailing Address | Where to mail Form 940?"
          description="Find out the Form 940 mailing address for 2025 based on your location. Filing with or without a payment, use the correct address to ensure timely processing."
          keywords=""
          canonical="resources/employment-tax/form-940-mailing-address/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <ExitPopup940 />
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 940</span>
          </span>{' '}
          Mailing Address
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, sm: 7.5, md: 8.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-940-mailing-address/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 940 Mailing address at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-940-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>Form 940</b>
                        </Link>
                        {
                          ' is filed annually with the IRS by employers to report '
                        }
                        <b>Federal Unemployment Tax Act (FUTA) tax</b>
                        {'. If you choose to '}
                        <b>paper file Form 940</b>
                        {', the correct IRS mailing address depends on your '}
                        <b>
                          business location and whether you are including a
                          payment
                        </b>
                        {'. '}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <b>Mailing Address:</b>
                        {
                          ' Employers should use the IRS mailing address assigned to their state and filing situation. Returns '
                        }
                        <b>with payments</b>
                        {' are generally mailed to the IRS address in '}
                        <b>Louisville, KY</b>
                        {', while returns '}
                        <b>without payments</b>
                        {' are generally sent to '}
                        <b>Kansas City, MO, or Ogden, UT</b>
                        {", depending on the employer's location. "}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/irs-form-940-due-dates/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>Due Date</b>
                        </Link>
                        <b>:</b>
                        {' Form 940 is'} {'due by '}
                        <b>January 31</b>
                        {' following the end of the calendar year. For the '}
                        <b>
                          {'2025 tax year, the Form 940 due date is February '}
                        </b>
                        <b>0</b>
                        <b>2, 2026</b>
                        {', because January 31 falls on a weekend. '}
                      </span>
                    </li>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <Link
                        href="/resources/employment-tax/form-940-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Form 940
                      </Link>
                      , the Employer’s Annual Federal Unemployment (FUTA) Tax
                      Return, is used by employers to report and pay federal
                      unemployment taxes. This tax helps fund unemployment
                      benefits for eligible workers who have lost their jobs and
                      is paid entirely by the employer. It is filed{' '}
                      <span style={{ fontWeight: 600 }}>annually</span> with the
                      IRS.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                      Employers must report total wages paid to employees, FUTA
                      tax liability, and any adjustments or credits claimed
                      during the year. Even if no FUTA tax is owed, eligible
                      employers may still be required to file the form.
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                  }}
                  className="gradient-bg"
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '20px !important',
                      }}
                    >
                      The IRS prefers e-filing for accuracy and faster
                      processing.
                    </Typography>
                    <Typography variant="body2">
                      E-file Form 940 (FUTA) with TaxZerone in minutes and get
                      instant IRS filing updates.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                        backgroundColor: '#0bf473',
                        color: '#333',
                        fontSize: 18,
                        fontWeight: 600,
                        '&:hover, &:focus, &:active': {
                          backgroundColor: '#fff',
                        },
                      }}
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=940mail`}
                    >
                      E-File Form 940
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      backgroundColor: '#fff3b1',
                      py: 1,
                      px: 2,
                      border: '2px dashed #000',
                      color: '#000',
                      lineHeight: 1.3,
                    }}
                  >
                    <b>Save 10%</b> on your filing fee — use code{' '}
                    <b style={{ fontSize: 18, color: '#c02121' }}>TZOEMP10</b>{' '}
                    at checkout.
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to mail your Form 940?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you choose to{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-940/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 940
                  </Link>{' '}
                  by paper, the IRS mailing address depends on whether you are
                  including a payment with your return and the state where your
                  business is located.
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> E-filing reduces processing time, minimizes
                  errors, and eliminates mailing delays.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Mailing Addresses for Form 940
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table aria-label="Mailing Address Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{ color: '#444', width: 200, fontWeight: 600 }}
                          >
                            If you’re in...
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 200, fontWeight: 600 }}
                          >
                            Without a payment
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 200, fontWeight: 600 }}
                          >
                            With a payment
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {/* Row 1 */}
                        <TableRow>
                          <TableCell>
                            Connecticut, Delaware, District of Columbia,
                            Georgia, Illinois, Indiana, Kentucky, Maine,
                            Maryland, Massachusetts, Michigan, New Hampshire,
                            New Jersey, New York, North Carolina, Ohio,
                            Pennsylvania, Rhode Island, South Carolina,
                            Tennessee, Vermont, Virginia, West Virginia,
                            Wisconsin
                          </TableCell>

                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                            </span>
                            <br />
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            Kansas City, MO 64999-0046
                          </TableCell>

                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932000
                            <br />
                            Louisville, KY 40293-2000
                          </TableCell>
                        </TableRow>

                        {/* Row 2 */}
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alabama, Alaska, Arizona, Arkansas, California,
                            Colorado, Florida, Hawaii, Idaho, Iowa, Kansas,
                            Louisiana, Minnesota, Mississippi, Missouri,
                            Montana, Nebraska, Nevada, New Mexico, North Dakota,
                            Oklahoma, Oregon, South Dakota, Texas, Utah,
                            Washington, Wyoming
                          </TableCell>

                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                            </span>
                            <br />
                            Internal Revenue Service
                            <br />
                            Ogden, UT 84201-0046
                          </TableCell>

                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932000
                            <br />
                            Louisville, KY 40293-2000
                          </TableCell>
                        </TableRow>

                        {/* Row 3 */}
                        <TableRow>
                          <TableCell>
                            Puerto Rico, U.S. Virgin Islands
                          </TableCell>

                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 409101
                            <br />
                            Ogden, UT 84409
                          </TableCell>

                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932000
                            <br />
                            Louisville, KY 40293-2000
                          </TableCell>
                        </TableRow>

                        {/* Row 4 */}
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            If the location of your legal residence, principal
                            place of business, office, or agency is not listed
                          </TableCell>

                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 409101
                            <br />
                            Ogden, UT 84409
                          </TableCell>

                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932000
                            <br />
                            Louisville, KY 40293-2000
                          </TableCell>
                        </TableRow>

                        {/* Row 5 */}
                        <TableRow>
                          <TableCell>
                            EXCEPTION for tax-exempt organizations; federal,
                            state, and local governments; and Indian tribal
                            governments, regardless of your location
                          </TableCell>

                          <TableCell>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                            </span>
                            <br />
                            Internal Revenue Service
                            <br />
                            Ogden, UT 84201-0046
                          </TableCell>

                          <TableCell>
                            <span style={{ fontWeight: 500 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 932000
                            <br />
                            Louisville, KY 40293-2000
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Mail at least 3–5 days before the{' '}
                    <Link
                      href="/resources/employment-tax/irs-form-940-due-dates/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 940 due date
                    </Link>{' '}
                    <span style={{ fontWeight: 600 }}>(February 2, 2026)</span>{' '}
                    to ensure it reaches the IRS on time. Form 941
                  </li>
                  <li>
                    For the IRS mailing address to use if you’re using a Private
                    Delivery Service (PDS), go to the{' '}
                    <Link
                      href="https://www.irs.gov/pdsstreetaddresses"
                      sx={{ color: '#0202ff' }}
                    >
                      IRS street address
                    </Link>
                    .{' '}
                  </li>
                  <li>
                    If you prefer to pay the amount you owe by money order or
                    check, send Form 940-V along with your Form 940.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Why E-Filing is better than Paper Filing?
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table aria-label="E-Filing vs Paper Filing Comparison">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{ color: '#444', width: 200, fontWeight: 600 }}
                          >
                            Feature
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 200, fontWeight: 600 }}
                          >
                            E-Filing
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 200, fontWeight: 600 }}
                          >
                            Paper Filing
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Processing Speed
                          </TableCell>
                          <TableCell>
                            Processed quickly with faster IRS handling
                          </TableCell>
                          <TableCell>
                            Slower processing due to manual handling
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Submission Confirmation
                          </TableCell>
                          <TableCell>
                            Instant IRS notifications about filing status
                          </TableCell>
                          <TableCell>
                            No immediate confirmation; delivery delays possible
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Accuracy
                          </TableCell>
                          <TableCell>
                            Built-in error checks reduce mistakes
                          </TableCell>
                          <TableCell>Higher risk of manual errors</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Security
                          </TableCell>
                          <TableCell>
                            Encrypted and securely transmitted to the IRS
                          </TableCell>
                          <TableCell>
                            Risk of lost, damaged, or misdelivered mail
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Cost</TableCell>
                          <TableCell>No printing or postage required</TableCell>
                          <TableCell>
                            Printing, envelopes, and mailing costs apply
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Deadline Flexibility
                          </TableCell>
                          <TableCell>
                            File up to the deadline, even after business hours
                          </TableCell>
                          <TableCell>
                            Must mail days in advance to meet deadlines
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Recordkeeping
                          </TableCell>
                          <TableCell>
                            Digital copies stored and easy to access
                          </TableCell>
                          <TableCell>
                            Paper copies must be manually stored
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            IRS Preference
                          </TableCell>
                          <TableCell>IRS-recommended method</TableCell>
                          <TableCell>Less efficient and outdated</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  E-File your IRS Form 940 using TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Follow these 3 steps to file accurately with TaxZerone.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.4,
                  }}
                >
                  Step 1:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select the tax year and input your business information.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mmb: 0.5,
                    lineHeight: 1.4,
                  }}
                >
                  Step 2:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Type the FUTA wages and tax information. TaxZerone
                  automatically calculates FUTA liability instantly and checks
                  your entries to make sure they are correct. You may also
                  choose to pay the balance due electronically.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.4,
                  }}
                >
                  Step 3:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Transmit your return to the IRS and receive instant filing
                  status updates.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        TThe information provided is based on guidelines from
                        the IRS website. For more information, check the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i940.pdf"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        Instructions for Form 940.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-File your FUTA tax form in 3 simple steps with TaxZerone.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              TaxZerone makes the process faster, accurate calculations, and
              instant IRS confirmation.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=940mail`}
              >
                E-File Form 940
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form941MailAddress;
